import { notification } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Header from "shared/components/Header";
import ProcessStepper from "shared/components/ProcessStepper";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { SEND_DYNAMIC_QUOTE } from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COST } from "utils/api/graphql/queries/general-cost";
import { TRACER } from "utils/api/graphql/queries/tracers";
import View from "./View";

const steps = [
  { title: "Qualification" },
  { title: "Sélection<br />produits" },
  { title: "Devis" },
  { title: "Souscription" },
  { title: "Documents" },
  { title: "Signature<br />En attente" },
  { title: "Souscription terminée" },
];

const Quote = ({ onNext, onBack, currentStatus, project }) => {
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    project,
  });
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const TRANSLATION = t("DETAIL.TEMPLATES.LAFOURGALE.QUOTE", {
    returnObjects: true,
  });

  const [getTracer] = useLazyQuery(TRACER);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [sendQuoteMutation] = useMutation(SEND_DYNAMIC_QUOTE, {
    onCompleted: (_) => {
      notification.open({
        message: "Devis envoyé avec succès",
        duration: 5,
        type: "success",
      });
      onNext({
        payload: {
          locked: true,
        },
      });
    },
  });

  const { data, loading: generalCostLoading } = useQuery(GENERAL_COST, {
    variables: {
      where: {
        project: { id: get(project, "id") },
        insurancePlan: { id: get(project, "fields.insurancePlans.0") },
      },
    },
  });

  const handleSubmit = async ({ template }) => {
    setLoading(true);
    generateQuoteDocuments({
      variables: {
        data: {
          project: { id: project.id },
          insurancePlans: get(project, "fields.insurancePlans", []).map(
            (id) => ({ id }),
          ),
        },
      },
      onCompleted: async (_) => {
        const selectedData = get(project, "fields.insurancePlans", []);
        const createSubscription = async (insurancePlanId) =>
          addSubscription({
            variables: {
              data: {
                id: cuid(),
                project: { id: project.id },
                insurancePlan: { id: insurancePlanId },
              },
            },
          });

        await Promise.all(selectedData.map(createSubscription));
        await sendQuoteMutation({
          variables: {
            data: {
              links: {},
              project: { id: project.id },
              insurancePlans: selectedData.map((id) => ({ id })),
              template,
            },
          },
        });
        setLoading(false);
      },
    });
  };

  if (generalCostLoading) return <Loading />;

  return (
    <>
      <Header
        key="header"
        title={TRANSLATION.GENERATION_HEADER_TITLE}
        actions={[]}
      />
      <ProcessStepper
        steps={steps}
        current={+get(currentStatus, "order")}
        aria-label="Étapes du projet"
      />
      <View
        translation={TRANSLATION}
        generalCost={omitDeep(get(data, "generalCost"), "__typename")}
        project={project}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        setInitialValues={setInitialValues}
        onBack={onBack}
        loading={loading}
      />
    </>
  );
};

export default Quote;
